import React, { useEffect, useState, useCallback, useMemo } from "react";
import { listAuditEntries, listAuditFacets } from "./adminService";
import "./AuditScreen.css";

/* ── Inline SVG icons ── */
const Icon = ({ size = 16, fill = "none", stroke = "currentColor", strokeWidth = 1.75, children, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke}
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false" {...rest}>{children}</svg>
);
const IconSearch = (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>;
const IconChevronDown = (p) => <Icon {...p}><path d="m6 9 6 6 6-6" /></Icon>;
const IconChevronLeft = (p) => <Icon {...p}><path d="m15 18-6-6 6-6" /></Icon>;
const IconChevronRight = (p) => <Icon {...p}><path d="m9 18 6-6-6-6" /></Icon>;
const IconAlert = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16v.01" /></Icon>;
const IconShield = (p) => <Icon {...p}><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /></Icon>;
const IconRefresh = (p) => <Icon {...p}><path d="M21 12a9 9 0 0 1-15.36 6.36L3 16M3 12a9 9 0 0 1 15.36-6.36L21 8M3 4v4h4M21 20v-4h-4" /></Icon>;
const IconUser = (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>;
const IconClock = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const IconHash = (p) => <Icon {...p}><path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" /></Icon>;

/* ── Action label / category mapping ── */
const ACTION_META = {
  "booking.confirm":               { label: "Confirm booking",      cls: "audit-pill--ok",     emoji: "" },
  "booking.decline":               { label: "Decline booking",      cls: "audit-pill--danger", emoji: "" },
  "booking.assign_provider":       { label: "Assign provider",      cls: "audit-pill--accent", emoji: "" },
  "booking.bulk_confirm":          { label: "Bulk confirm",         cls: "audit-pill--ok",     emoji: "" },
  "booking.bulk_decline":          { label: "Bulk decline",         cls: "audit-pill--danger", emoji: "" },
  "booking.bulk_action":           { label: "Bulk action",          cls: "audit-pill--neutral",emoji: "" },
  "etransfer.manual_match":        { label: "Manual e-Transfer",    cls: "audit-pill--accent", emoji: "" },
  "provider.verify_decision":      { label: "Verify provider",      cls: "audit-pill--accent", emoji: "" },
  "provider.profile_change_review":{ label: "Review profile change",cls: "audit-pill--accent", emoji: "" },
  "provider.create":               { label: "Create provider",      cls: "audit-pill--accent", emoji: "" },
  "provider.delete":               { label: "Delete provider",      cls: "audit-pill--danger", emoji: "" },
};
const actionMeta = (a) => ACTION_META[a] || { label: a, cls: "audit-pill--neutral" };

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit",
  });
};
const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const AuditScreen = ({ globalSearch = "" }) => {
  const token = localStorage.getItem("@uphealth_login_token");

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 25, totalItems: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const [facets, setFacets] = useState({ actions: [], admins: [] });

  /* Debounce search */
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(h);
  }, [searchTerm]);

  const effectiveSearch = useMemo(() => debouncedSearch || globalSearch, [debouncedSearch, globalSearch]);

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const opts = { page, limit: pagination.pageSize };
      if (effectiveSearch) opts.search = effectiveSearch;
      if (actionFilter)  opts.action = actionFilter;
      if (adminFilter)   opts.admin_id = adminFilter;
      if (resultFilter)  opts.result = resultFilter;
      if (fromDate)      opts.from = fromDate;
      if (toDate)        opts.to = toDate;

      const res = await listAuditEntries(token, opts);
      setRows(res.data?.data || []);
      setPagination(res.data?.pagination || pagination);
    } catch (err) {
      console.error(err);
      setError("Failed to load audit log.");
    }
    setLoading(false);
  }, [token, pagination.pageSize, effectiveSearch, actionFilter, adminFilter, resultFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Initial + filter-change fetch */
  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  /* Load facets once */
  useEffect(() => {
    let cancelled = false;
    listAuditFacets(token)
      .then((res) => { if (!cancelled) setFacets(res.data || { actions: [], admins: [] }); })
      .catch((err) => console.error("Facet load failed:", err));
    return () => { cancelled = true; };
  }, [token]);

  const clearFilters = () => {
    setActionFilter("");
    setAdminFilter("");
    setResultFilter("");
    setFromDate("");
    setToDate("");
    setSearchTerm("");
  };

  const handleRowClick = (auditId) => {
    setExpandedId(expandedId === auditId ? null : auditId);
  };

  if (error) {
    return <div className="alert alert-danger" role="alert">{error}</div>;
  }

  const hasFilters = !!(actionFilter || adminFilter || resultFilter || fromDate || toDate || searchTerm);

  return (
    <div className="audit-container">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-header__title">
            <IconShield size={20} style={{ marginRight: 8, verticalAlign: "-3px", color: "var(--teal)" }} />
            Audit Log
          </h1>
          <div className="page-header__subtitle">
            <strong style={{ color: "var(--text-1)" }}>{pagination.totalItems}</strong> recorded actions
            {hasFilters && <> · filtered</>}
          </div>
        </div>
        <div>
          <button className="audit-btn" onClick={() => fetchData(pagination.currentPage)} title="Refresh">
            <IconRefresh size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <div className="audit-search">
          <IconSearch size={14} />
          <input
            type="text"
            placeholder="Search action, target id, admin email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search audit log"
          />
        </div>

        <div className="audit-select">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} aria-label="Filter by action">
            <option value="">All actions</option>
            {facets.actions.map((a) => (
              <option key={a.action} value={a.action}>
                {actionMeta(a.action).label} ({a.count})
              </option>
            ))}
          </select>
          <IconChevronDown size={12} />
        </div>

        <div className="audit-select">
          <select value={adminFilter} onChange={(e) => setAdminFilter(e.target.value)} aria-label="Filter by admin">
            <option value="">All admins</option>
            {facets.admins.map((a) => (
              <option key={a.admin_account_id} value={a.admin_account_id}>
                {a.admin_email || `#${a.admin_account_id}`} ({a.count})
              </option>
            ))}
          </select>
          <IconChevronDown size={12} />
        </div>

        <div className="audit-select">
          <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} aria-label="Filter by result">
            <option value="">Any result</option>
            <option value="success">Success</option>
            <option value="partial">Partial</option>
            <option value="failed">Failed</option>
          </select>
          <IconChevronDown size={12} />
        </div>

        <div className="audit-date">
          <label>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="audit-date">
          <label>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        {hasFilters && (
          <button className="audit-clear" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="audit-shell">
        {loading ? (
          <div className="audit-loading"><span className="audit-spinner" /> Loading audit log…</div>
        ) : rows.length === 0 ? (
          <div className="audit-empty">
            <IconShield size={28} />
            <h4>No audit entries found</h4>
            <p>{hasFilters ? "Try removing or adjusting filters." : "Admin actions will appear here as they're performed."}</p>
          </div>
        ) : (
          <ul className="audit-list">
            {rows.map((r) => {
              const meta = actionMeta(r.action);
              const expanded = expandedId === r.audit_id;
              return (
                <li key={r.audit_id} className={`audit-row ${expanded ? "audit-row--expanded" : ""}`}>
                  <button type="button" className="audit-row__main" onClick={() => handleRowClick(r.audit_id)}>
                    <div className="audit-row__time">
                      <div className="audit-row__abs">{fmtDateTime(r.created_at)}</div>
                      <div className="audit-row__rel"><IconClock size={10} /> {fmtRelative(r.created_at)}</div>
                    </div>
                    <div className="audit-row__main-text">
                      <div className="audit-row__line">
                        <span className={`audit-pill ${meta.cls}`}>{meta.label}</span>
                        {r.target_type && r.target_id && (
                          <span className="audit-row__target">
                            <IconHash size={10} /> {r.target_type}:{r.target_id.length > 16 ? r.target_id.slice(-12) : r.target_id}
                          </span>
                        )}
                        {r.result === "failed" && (
                          <span className="audit-pill audit-pill--danger">
                            <IconAlert size={10} /> failed
                          </span>
                        )}
                        {r.result === "partial" && (
                          <span className="audit-pill audit-pill--warn">partial</span>
                        )}
                      </div>
                      <div className="audit-row__sub">
                        <IconUser size={11} />
                        {r.admin_email || (r.admin_account_id ? `Admin #${r.admin_account_id}` : "Unknown actor")}
                        {r.ip_address && <> · <span className="audit-row__mono">{r.ip_address}</span></>}
                      </div>
                    </div>
                    <IconChevronDown size={14} className={`audit-row__chev ${expanded ? "audit-row__chev--open" : ""}`} />
                  </button>
                  {expanded && (
                    <div className="audit-row__detail">
                      <div className="audit-row__detail-grid">
                        <Field label="Audit ID" mono>{r.audit_id}</Field>
                        <Field label="Action" mono>{r.action}</Field>
                        <Field label="Target type">{r.target_type || "—"}</Field>
                        <Field label="Target id" mono>{r.target_id || "—"}</Field>
                        <Field label="Result">{r.result}</Field>
                        <Field label="When">{fmtDateTime(r.created_at)}</Field>
                        <Field label="Admin">{r.admin_email || `#${r.admin_account_id || "?"}`}</Field>
                        <Field label="IP" mono>{r.ip_address || "—"}</Field>
                        <Field label="User-Agent" mono fullWidth>{r.user_agent || "—"}</Field>
                        {r.error && <Field label="Error" fullWidth danger>{r.error}</Field>}
                        {r.payload && (
                          <Field label="Payload" fullWidth>
                            <pre className="audit-payload">
                              {JSON.stringify(r.payload, null, 2)}
                            </pre>
                          </Field>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {!loading && rows.length > 0 && (
          <div className="audit-footer">
            <div>
              Page <strong>{pagination.currentPage}</strong> of {pagination.totalPages}
              <span style={{ margin: "0 10px", color: "var(--border-3)" }}>·</span>
              <strong>{pagination.totalItems}</strong> total
            </div>
            <div className="audit-pager">
              <button
                className="audit-pager__btn"
                onClick={() => fetchData(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1 || loading}
                aria-label="Previous page"
              >
                <IconChevronLeft size={14} />
              </button>
              <button
                className="audit-pager__btn"
                onClick={() => fetchData(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages || loading}
                aria-label="Next page"
              >
                <IconChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, mono, fullWidth, danger }) => (
  <div className={`audit-field ${fullWidth ? "audit-field--full" : ""} ${danger ? "audit-field--danger" : ""}`}>
    <div className="audit-field__label">{label}</div>
    <div className={`audit-field__value ${mono ? "audit-field__value--mono" : ""}`}>{children}</div>
  </div>
);

export default AuditScreen;
